import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {DbActiveModal} from "../../../modules/modal/modal-ref";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {Config} from "../../../config/config";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../store";
import {combineLatest} from "rxjs";
import {DbValidators} from "../../../shared/validators/DbValidators";
import {CreateWebsiteHelpersService} from "../../../services/local/create-website-helpers.service";
import {OptionRegion} from "../../../shared/models/option-region.model";
import {DbModalService} from "../../../modules/modal/modal";
import {HostingService} from "../../../websites/website/hosting/hosting.service";
import {
  MigrationSuccessDialogComponent
} from "../../../websites/add-website-dialog/migration-success-dialog/migration-success-dialog.component";
import {
  MigrationRestApiBlockedComponent
} from "../../../shared/components/dialogs/migration-rest-api-blocked/migration-rest-api-blocked.component";
import {PerformanceService} from "../../../websites/website/performance/performance.service";
import {ActivityStatus, IActivity} from "../../../activities/activity";
import {
  ManualMigrationDialogComponent
} from "../../../shared/components/dialogs/migration/manual-migration-dialog/manual-migration-dialog.component";
import {DialogService} from "../../../../ng2-bootstrap-modal/dialog.service";
import {GoogleAnalyticsService} from "../../../shared/services/google-analytics/google-analytics.service";
import {UserSubscriptionCategory} from "../../../user/user-subscription";

@Component({
    selector: 'db-migrate-website',
    templateUrl: './migrate-website.component.html',
    styleUrls: ['./migrate-website.component.scss'],
    standalone: false
})
export class MigrateWebsiteComponent implements OnInit, OnDestroy {

  @Input() websiteId: number;
  @Input() showManualMigration = true;
  @Input() migrationProcess = false;
  @Input() websiteUrl?: string;
  @Input() zipUploading?: boolean = false;
  public formSubmitted = false;
  public loading = false;
  public configHostname = Config.hostname;
  public regions: OptionRegion[] = [];
  public hostedWebsitesLimit = 0;
  public initialDataLoading = true;
  public displayWebsiteUrl: string;
  public migrateWebsiteForm = new FormGroup({
    subdomain: new FormControl('', [DbValidators.trim, Validators.required, Validators.pattern('^([a-z0-9]+(-[a-z0-9]+)*)$')]),
    region: new FormControl(null, Validators.required),
  });

  private readonly _destroyStream$ = new Subject<boolean>();
  protected readonly UserSubscriptionCategory = UserSubscriptionCategory;


  constructor(
    private readonly _activeModal: DbActiveModal,
    private readonly _performanceService: PerformanceService,
    private readonly _modalService: DbModalService,
    private readonly _store: Store<fromStore.DbStoreState>,
    private readonly _createWebsiteHelpersService: CreateWebsiteHelpersService,
    private readonly _hostingService: HostingService,
    private readonly _dialogService: DialogService,
    private readonly _googleAnalyticsService: GoogleAnalyticsService
  ) {
  }

  ngOnInit() {
    this._activeModal.resize(600, 1000);
    this._activeModal.whiteCloseBtn(window.innerWidth > 991, 0.5);
    this.displayWebsiteUrl = this.websiteUrl ? this.websiteUrl.replace(/^https?:\/\//i, '') : '';
    this._subdomainStatusHandler();
    this._runInitialSubscriptions();
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private _resizeModal(): void {
    let height = 364;
    if (this.formSubmitted && this.migrateWebsiteForm.get('subdomain').invalid) {
      height += 20;
    }
    this._activeModal.resize(height, 700);
  }

  private _subdomainStatusHandler(): void {
    this.migrateWebsiteForm.get('subdomain').statusChanges
      .pipe(takeUntil(this._destroyStream$))
      .subscribe(() => this.formSubmitted && this._resizeModal());
  }

  private _runInitialSubscriptions(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserSubscription),
        )),
        first()
      )
      .subscribe(([user, userSubscription]) => {
        this.hostedWebsitesLimit = user && user.hosting_services && user.hosting_services.domain_count && userSubscription && userSubscription.plan ? parseInt(user.hosting_services.domain_count.parameter_value, 10) + user.additionalWebsiteCount : null;
        this._getInitialData(user.subscriptionId);
      });
  }

  private _getInitialData(subscriptionId): void {
    this._createWebsiteHelpersService.getWebsiteCreationInitialData(subscriptionId)
      .then(([suggestedSubDomain, regions]) => {
        this.migrateWebsiteForm.get('subdomain').setValue(suggestedSubDomain);
        this.regions = regions;
        const region = regions.find(_region => _region.isAvailable);
        this.migrateWebsiteForm.get('region').setValue(region.value);
        this.initialDataLoading = false;
      });
  }

  public showManualMigrationModal(failed = false): void {
    this._activeModal.close();
    this._dialogService.addDialog(ManualMigrationDialogComponent, {ErrMessage: failed});
  }

  public submit(): void {
    this.formSubmitted = true;
    if (this.migrateWebsiteForm.invalid) return;
    this.loading = true;
    this._createWebsiteHelpersService.checkSubDomain(this.migrateWebsiteForm.value.subdomain)
      .then(response => {
        if (response.success) {
          this._migrate();
        } else {
          this.migrateWebsiteForm.get('subdomain').setErrors({taken: response.message});
          this.loading = false;
        }
      });

  }

  private _migrate() {
    this._hostingService.autoMigration(this.migrateWebsiteForm.value, this.websiteId, false)
      .pipe(
        first(response => !!response),
      )
      .subscribe(response => {
        if (response.success) {
          this.migrationProcess = true;
        } else if (response.response && response.response['error'].data.rest_route === 'not_available') {
          this._restBlocked();
        } else {
          this.showManualMigrationModal(true);
        }
      });
  }

  private _restBlocked(): void {
    this._activeModal.close();
    const modal = this._modalService.open(MigrationRestApiBlockedComponent);
    modal.componentInstance.formData = this.migrateWebsiteForm.value;
    modal.componentInstance.websiteId = this.websiteId;
    modal.componentInstance.hasCloseBtn = true;
  }

  public resizeModal(height, width): void {
    this._activeModal.resize(height, width);
  }

  public showMigrationSuccessModal({websiteId, data}: {
    websiteId: number,
    data: any
  }, zipUploading: boolean = false): void {
    if (zipUploading && data && data.id) {
      this._performanceService.getGooglePageSpeedScore(data.id);
      this._getScoreOngoingActivity(data.id);
    } else {
      data.migrating_domain_id && this._countScore(data.migrating_domain_id, data);
    }

  }

  private _getScoreOngoingActivity(id): void {
    this._store.select(fromStore.getGoogleScoreOngoingActivity(id))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe((websiteScore: IActivity): void => {
        if (websiteScore && websiteScore.status === ActivityStatus.DONE) {
          let scoreData: any = {};
          if (websiteScore && websiteScore.meta && websiteScore.meta.current_score) {
            scoreData = Object.assign({}, scoreData, {
              score: websiteScore && websiteScore.meta && websiteScore.meta.current_score
            });
          }
          id && this._countScore(id, scoreData);
        }
      });
  }

  private _countScore(id, data): void {
    this._store.select(fromStore.getWebsite(Number(id)))
      .pipe(
        first(website => !!website)
      )
      .subscribe(website => {
        const _websiteOld = website;
        const siteUrlOld = _websiteOld ? _websiteOld.site_url : '';
        const scoreOld = _websiteOld && _websiteOld.score && _websiteOld.score.originalScore && _websiteOld.score.originalScore.desktopScore;
        const mobileScoreOld = _websiteOld && _websiteOld.score && _websiteOld.score.originalScore && _websiteOld.score.originalScore.mobileScore;
        const desktopTTIOld = _websiteOld && _websiteOld.score && _websiteOld.score.originalScore && _websiteOld.score.originalScore.desktopTTI;
        const mobileTTIOld = _websiteOld && _websiteOld.score && _websiteOld.score.originalScore && _websiteOld.score.originalScore.mobileTTI;
        const oldData = {
          'websiteSiteUrl': siteUrlOld,
          'score': scoreOld,
          'desktopTTI': desktopTTIOld,
          'mobileScore': mobileScoreOld,
          'mobileTTI': mobileTTIOld,
        };
        const newData = {
          'websiteId': id,
          'websiteSiteUrl': website.site_url ? website.site_url : (data.score && data.score.url),
          'score': data.score && data.score.desktop_score,
          'desktopTTI': data.score && data.score.desktop_tti,
          'mobileScore': data.score && data.score.mobile_score,
          'mobileTTI': data.score && data.score.mobile_tti
        };

        this._showSuccess(oldData, newData);
      });
  }

  private _showSuccess(oldData, newData): void {
    this._activeModal.close();
    // if (!this._modalService.hasOpenModals()) {
      const modal = this._modalService.open(MigrationSuccessDialogComponent);
      modal.componentInstance.oldData = oldData;
      modal.componentInstance.newData = newData;
    // }
  }

}
